import { normalizeProjectPath } from "@repo/shared";
import { ALLOWED_PROJECT_SEARCH_PARAMS } from "@/lib/project-search-param-keys";

export type TabSearchParams = Record<string, unknown>;

export interface TabMemory {
  entityPath?: string;
  entityIconKey?: string;
  assistantPath?: string;
  assistantDraftMarkdown?: string;
}

export type WorkspaceKind = "project" | "admin";

export interface ProjectWorkspaceTab {
  kind: "project";
  tabId: string;
  resourceId: string;
  name: string;
  currentPath: string;
  searchParams: TabSearchParams;
  memory: TabMemory;
}

export interface AdminWorkspaceTab {
  kind: "admin";
  tabId: typeof ADMIN_TAB_ID;
  name: typeof ADMIN_TAB_NAME;
  currentPath: string;
  searchParams: TabSearchParams;
  memory: TabMemory;
}

export type WorkspaceTab = ProjectWorkspaceTab | AdminWorkspaceTab;

export interface WorkspaceLocationMatch {
  kind: WorkspaceKind;
  tabId: string;
  resourceId: string | null;
  currentPath: string;
}

export type ProjectWorkspaceNavigationTarget =
  | {
      kind: "current-project";
      currentPath: string;
    }
  | {
      kind: "app";
      href: string;
    };

interface SanitizeSearchParamsOptions {
  preserveUndefined?: boolean;
}

const PROJECT_ROUTE_PREFIX = "/project/";
const ADMIN_ROUTE_PREFIX = "/df-admin";

export const ADMIN_TAB_ID = "admin";
export const ADMIN_TAB_NAME = "Admin Portal";

export function normalizeWorkspacePath(path: string): string {
  return normalizeProjectPath(path);
}

export function makeProjectTabId(resourceId: string): string {
  return `project:${resourceId}`;
}

export function getDefaultWorkspacePath(kind: WorkspaceKind): string {
  return kind === "admin" ? "/skills" : "/";
}

export function resolveWorkspacePath(kind: WorkspaceKind, path: string): string {
  const normalizedPath = normalizeWorkspacePath(path);
  if (kind === "admin" && normalizedPath === "/") {
    return getDefaultWorkspacePath(kind);
  }
  return normalizedPath;
}

export function isProjectWorkspaceTab(tab: WorkspaceTab): tab is ProjectWorkspaceTab {
  return tab.kind === "project";
}

export function isAdminWorkspaceTab(tab: WorkspaceTab): tab is AdminWorkspaceTab {
  return tab.kind === "admin";
}

export function sanitizeProjectSearchParams(params: TabSearchParams | null | undefined): {
  searchParams: TabSearchParams;
  changed: boolean;
};
export function sanitizeProjectSearchParams(
  params: TabSearchParams | null | undefined,
  options: SanitizeSearchParamsOptions
): {
  searchParams: TabSearchParams;
  changed: boolean;
};
export function sanitizeProjectSearchParams(
  params: TabSearchParams | null | undefined,
  options: SanitizeSearchParamsOptions = {}
): {
  searchParams: TabSearchParams;
  changed: boolean;
} {
  if (!params || typeof params !== "object" || Array.isArray(params)) {
    return { searchParams: {}, changed: false };
  }

  const { preserveUndefined = false } = options;
  const searchParams: TabSearchParams = {};
  let changed = false;

  for (const [key, value] of Object.entries(params)) {
    if (!ALLOWED_PROJECT_SEARCH_PARAMS.has(key)) {
      changed = true;
      continue;
    }

    if (value === undefined && !preserveUndefined) {
      changed = true;
      continue;
    }

    searchParams[key] = value;
  }

  if (!changed) {
    return {
      searchParams: params,
      changed: false,
    };
  }

  return {
    searchParams,
    changed: true,
  };
}

export function sanitizeAdminSearchParams(params: TabSearchParams | null | undefined): {
  searchParams: TabSearchParams;
  changed: boolean;
};
export function sanitizeAdminSearchParams(
  params: TabSearchParams | null | undefined,
  options: SanitizeSearchParamsOptions
): {
  searchParams: TabSearchParams;
  changed: boolean;
};
export function sanitizeAdminSearchParams(
  params: TabSearchParams | null | undefined,
  options: SanitizeSearchParamsOptions = {}
): {
  searchParams: TabSearchParams;
  changed: boolean;
} {
  if (!params || typeof params !== "object" || Array.isArray(params)) {
    return { searchParams: {}, changed: false };
  }

  const { preserveUndefined = false } = options;
  const searchParams: TabSearchParams = {};
  let changed = false;

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined && !preserveUndefined) {
      changed = true;
      continue;
    }

    searchParams[key] = value;
  }

  if (!changed) {
    return {
      searchParams: params,
      changed: false,
    };
  }

  return {
    searchParams,
    changed: true,
  };
}

export function sanitizeWorkspaceSearchParams(
  kind: WorkspaceKind,
  params: TabSearchParams | null | undefined,
  options: SanitizeSearchParamsOptions = {}
): {
  searchParams: TabSearchParams;
  changed: boolean;
} {
  return kind === "project"
    ? sanitizeProjectSearchParams(params, options)
    : sanitizeAdminSearchParams(params, options);
}

export function sanitizeTabMemory(memory: TabMemory | null | undefined): TabMemory {
  if (!memory || typeof memory !== "object" || Array.isArray(memory)) {
    return {};
  }

  const sanitized: TabMemory = {};
  if (typeof memory.entityPath === "string" && memory.entityPath.length > 0) {
    sanitized.entityPath = memory.entityPath;
  }
  if (typeof memory.entityIconKey === "string" && memory.entityIconKey.length > 0) {
    sanitized.entityIconKey = memory.entityIconKey;
  }
  if (typeof memory.assistantPath === "string" && memory.assistantPath.length > 0) {
    sanitized.assistantPath = memory.assistantPath;
  }
  if (
    typeof memory.assistantDraftMarkdown === "string" &&
    memory.assistantDraftMarkdown.length > 0
  ) {
    sanitized.assistantDraftMarkdown = memory.assistantDraftMarkdown.slice(0, 10_000);
  }
  return sanitized;
}

export function createProjectWorkspaceTab(args: {
  resourceId: string;
  name: string;
  currentPath?: string;
  searchParams?: TabSearchParams;
  memory?: TabMemory;
}): ProjectWorkspaceTab {
  return {
    kind: "project",
    tabId: makeProjectTabId(args.resourceId),
    resourceId: args.resourceId,
    name: args.name,
    currentPath: resolveWorkspacePath("project", args.currentPath ?? "/"),
    searchParams: sanitizeProjectSearchParams(args.searchParams ?? {}).searchParams,
    memory: sanitizeTabMemory(args.memory),
  };
}

export function createAdminWorkspaceTab(args?: {
  currentPath?: string;
  searchParams?: TabSearchParams;
  memory?: TabMemory;
}): AdminWorkspaceTab {
  return {
    kind: "admin",
    tabId: ADMIN_TAB_ID,
    name: ADMIN_TAB_NAME,
    currentPath: resolveWorkspacePath(
      "admin",
      args?.currentPath ?? getDefaultWorkspacePath("admin")
    ),
    searchParams: sanitizeAdminSearchParams(args?.searchParams ?? {}).searchParams,
    memory: sanitizeTabMemory(args?.memory),
  };
}

export function matchWorkspaceLocation(pathname: string): WorkspaceLocationMatch | null {
  if (pathname.startsWith(PROJECT_ROUTE_PREFIX)) {
    const projectPath = pathname.slice(PROJECT_ROUTE_PREFIX.length);
    const [resourceId, ...restSegments] = projectPath.split("/");
    if (!resourceId) {
      return null;
    }

    const nestedPath = restSegments.length > 0 ? `/${restSegments.join("/")}` : "/";
    return {
      kind: "project",
      tabId: makeProjectTabId(resourceId),
      resourceId,
      currentPath: resolveWorkspacePath("project", nestedPath),
    };
  }

  if (pathname === ADMIN_ROUTE_PREFIX || pathname.startsWith(`${ADMIN_ROUTE_PREFIX}/`)) {
    const nestedPath = pathname.slice(ADMIN_ROUTE_PREFIX.length) || "/";
    return {
      kind: "admin",
      tabId: ADMIN_TAB_ID,
      resourceId: null,
      currentPath: resolveWorkspacePath("admin", nestedPath),
    };
  }

  return null;
}

export function buildWorkspacePath(tab: WorkspaceTab): string {
  if (tab.kind === "project") {
    return `${PROJECT_ROUTE_PREFIX}${tab.resourceId}${resolveWorkspacePath(tab.kind, tab.currentPath)}`;
  }

  return `${ADMIN_ROUTE_PREFIX}${resolveWorkspacePath(tab.kind, tab.currentPath)}`;
}

function parseInternalAppHref(pathOrHref: string): { href: string; pathname: string } | null {
  if (!pathOrHref) {
    return null;
  }

  if (pathOrHref.startsWith("/")) {
    const hashIndex = pathOrHref.indexOf("#");
    const pathAndSearch = hashIndex >= 0 ? pathOrHref.slice(0, hashIndex) : pathOrHref;
    const searchIndex = pathAndSearch.indexOf("?");
    const pathname = searchIndex >= 0 ? pathAndSearch.slice(0, searchIndex) : pathAndSearch;
    return pathname ? { href: pathOrHref, pathname } : null;
  }

  try {
    const url = new URL(pathOrHref);
    const href = `${url.pathname}${url.search}${url.hash}`;
    return { href, pathname: url.pathname };
  } catch {
    return null;
  }
}

export function resolveProjectWorkspaceNavigationTarget(args: {
  projectResourceId: string;
  path: string;
}): ProjectWorkspaceNavigationTarget {
  const parsedAppHref = parseInternalAppHref(args.path);
  if (!parsedAppHref) {
    return {
      kind: "current-project",
      currentPath: args.path,
    };
  }

  const matchedWorkspace = matchWorkspaceLocation(parsedAppHref.pathname);

  if (matchedWorkspace?.kind === "project") {
    if (matchedWorkspace.resourceId === args.projectResourceId) {
      return {
        kind: "current-project",
        currentPath: matchedWorkspace.currentPath,
      };
    }

    return {
      kind: "app",
      href: parsedAppHref.href,
    };
  }

  if (matchedWorkspace?.kind === "admin") {
    return {
      kind: "app",
      href: parsedAppHref.href,
    };
  }

  return {
    kind: "current-project",
    currentPath: args.path,
  };
}
